/**
 * OrderService - Simple order processing
 */
import type { CheckoutData, CheckoutRequest } from "@/types/woocommerce.types";
import { set_cookie } from "@/lib/browser/cookie-utils";
import { COOKIE_NAMES } from "@/lib/constants/config";
import { link_prop_firm_click_to_order } from "@/services/integrations/prop-firm-match";
import { link_tapfiliate_click_to_order } from "@/services/integrations/tapfiliate";
import { get_cart_token } from "./cart-service";

/**
 * Creates an order in WooCommerce and returns the order data
 * For Stripe this also makes a payment intent
 * @param order_data - Order data object containing billing and payment info
 * @returns Promise<CheckoutData> - Order creation response
 */
export const create_order = async ({
  order_data,
}: {
  order_data: CheckoutRequest;
}): Promise<CheckoutData> => {
  validate_order_request(order_data);
  const normalized_order_data = normalize_order_request(order_data);
  const cart_token = get_cart_token();
  if (!cart_token) throw new Error("Cart session not found");

  const response = await fetch("/api/woocommerce/checkout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "Cart-Token": cart_token,
    },
    credentials: "same-origin",
    body: JSON.stringify(normalized_order_data),
  });

  const result = (await response.json()) as CheckoutData;

  console.log("checkout result:", result);

  if (result?.order_id) {
    set_cookie(COOKIE_NAMES.ORDER_ID, `${result.order_id}`, 1);
    await link_prop_firm_click_to_order(Number(result.order_id));
    await link_tapfiliate_click_to_order(Number(result.order_id));
  }
  return result;
};

const validate_order_request = (order_data: CheckoutRequest) => {
  if (!order_data) throw new Error("Order data is required");
  if (!order_data.billing_address)
    throw new Error("Required order data fields are missing");
};

const normalize_order_request = (
  order_data: CheckoutRequest,
): CheckoutRequest => {
  return {
    ...order_data,
    billing_address: {
      ...order_data.billing_address,
      country: order_data.billing_address?.country
        ? order_data.billing_address.country.toUpperCase()
        : order_data.billing_address?.country,
    },
  };
};
